import { subscriptionItems } from "constants/subscriptionItems";
import { consultants } from "constants/consultants";

export function parseDataToApi({
    gettingStarted,
    address,
    contactInfo,
    responsible,
    consultantId,
    expirationDate = '10',
    discountCoupon,
    paymentMethod,
    creditCardInfo,
    contele_tracker,
    contele_system,
    plug_and_play,
    tracker_2g,
    tracker_4g,
    blocking_12_volts,
    blocking_24_volts,
    fine_auto_update,
    vehicle_camera,
    vehicle_camera_ia,
    camera_setup,
    partial_shipment = false,
    first_batch_quantity = 0,
    vindi_customer_id,
    vindi_payment_profile_id,
}) {
    const hasNeededValues = [
        typeof gettingStarted === 'object',
        typeof expirationDate === 'string',
        typeof responsible === 'object',
        typeof address === 'object',
        typeof contactInfo === 'object',
    ].every(Boolean);

    if(!hasNeededValues){
        throw new Error('Missing/Invalid params');
    };

    const contele_customer = makeConteleCustomer({ 
        gettingStarted, 
        responsible, 
        consultantId,
        contactInfo, 
        address
    });

    const contele_subscription = makeConteleSubscription({
        contele_tracker,
        contele_system,
        plug_and_play,
        tracker_2g,
        tracker_4g,
        blocking_12_volts,
        blocking_24_volts,
        fine_auto_update,
        vehicle_camera,
        vehicle_camera_ia,
        camera_setup,
        discountCoupon,
        paymentMethod,
        expirationDate,
        partial_shipment,
        first_batch_quantity,
    });

    const contele_subscription_payload = {
        contele_customer,
        contele_subscription,
        ...(vindi_customer_id != null ? { vindi_customer_id } : {}),
        ...(vindi_payment_profile_id != null ? { vindi_payment_profile_id } : {}),
    };

    const paymentMethodIsCard = !!paymentMethod && paymentMethod === 'world_credit_card';

    if(paymentMethodIsCard && creditCardInfo){
        const [cardExpMonth, cardExpYear] = creditCardInfo.expirationDate.split('/');

        contele_subscription_payload.credit_card_to_pay = {
            card_number: creditCardInfo.cardNumber,
            holder_name: creditCardInfo.cardHolder,
            card_expiration_year: Number(cardExpYear),
            card_expiration_month: Number(cardExpMonth),
            card_cvv: creditCardInfo.cvv
        };
    };

    return contele_subscription_payload;
};

const makeConteleCustomer = ({ 
    gettingStarted, 
    responsible, 
    consultantId,
    contactInfo, 
    address 
}) => {
    const { document } = gettingStarted;

    const documentIsCpf = document && document.length <= 11;

    const documentType = documentIsCpf ? 'brasil_cpf' : 'brasil_cnpj';

    const { 
        company_name, 
        responsible: personalResponsible, 
        responsibleDocument 
    } = responsible;

    const people = makeSubscriptionPeoples({ 
        contactInfo, 
        consultantId, 
        responsibleDocument, 
        personalResponsible 
    });

    const addresses = makeSubscriptionAddress({ address });

    return {
        document_number: document,
        document_type: documentType,
        name: company_name || personalResponsible,
        people,
        addresses
    };
};

const makeConteleSubscription = ({
    contele_tracker,
    contele_system,
    plug_and_play,
    tracker_2g,
    tracker_4g,
    blocking_12_volts,
    blocking_24_volts,
    fine_auto_update,
    vehicle_camera,
    vehicle_camera_ia,
    camera_setup,
    discountCoupon,
    paymentMethod,
    expirationDate,
    partial_shipment,
    first_batch_quantity,
}) => {
    const itemsList = [];

    if(plug_and_play > 0){
        itemsList.push(['PLUGANDPLAY', plug_and_play]);
    };

    if(fine_auto_update){
        const totalDevices = (contele_tracker || 0) + (contele_system || 0) + (vehicle_camera || 0) + (vehicle_camera_ia || 0);
        itemsList.push(['FINE_MANAGER', totalDevices]);
    };
    
    if(contele_system > 0) {
        itemsList.push(['CONTELE_SYSTEM', contele_system]);
    }
    
    if(vehicle_camera) {
        itemsList.push(['CAMERA', vehicle_camera]);
    }

    if(vehicle_camera_ia) {
        itemsList.push(['CAMERA_IA', vehicle_camera_ia]);
    }

    if(camera_setup > 0) {
        itemsList.push(['CAMERA_SETUP', camera_setup]);
    }

    if(tracker_2g > 0) {
        itemsList.push(['TRACKERS_2G', tracker_2g]);
    }

    if(tracker_4g > 0) {
        itemsList.push(['TRACKERS_4G', tracker_4g]);
    }

    if(blocking_12_volts > 0) {
        itemsList.push(['BLOCKERS_12_VOLTS', blocking_12_volts]);
    }

    if(blocking_24_volts > 0) {
        itemsList.push(['BLOCKERS_24_VOLTS', blocking_24_volts]);
    }

    const items = makeSubscriptionItemByType({ items: itemsList });

    const billing_discounts = !!discountCoupon ? [{ code: discountCoupon }] : [];

    const payment_method = paymentMethod || 'world_bank_slip';

    return {
        items,
        billing_due_day_of_month: Number(expirationDate),
        billing_currency: 'BRL',
        billing_frequency: 'every_one_months',
        billing_discounts,
        payment_method,
        partial_shipment: !!partial_shipment,
        first_batch_quantity: partial_shipment ? (first_batch_quantity || 0) : 0,
    };
};

const makeSubscriptionPeoples = ({ contactInfo, consultantId, responsibleDocument, personalResponsible }) => {

    const managerInfo = {
        name: contactInfo.managerName,
        email: contactInfo.managerEmail,
        phone_number: contactInfo.managerPhone,
        phone_number_type: 'mobile',
        role: 'system_admin'
    };

    const financialManagerInfo = contactInfo.useManagerInfo ? {
        ...managerInfo,
        role: 'company_financial'
    } : {
        name: contactInfo.financeManagerName,
        email: contactInfo.financeManagerEmail,
        phone_number: contactInfo.financeManagerPhone,
        phone_number_type: 'mobile',
        role: 'company_financial'
    };

    const companyLegalRepresentative = {
        name: personalResponsible,
        document_number: responsibleDocument,
        document_type: 'brasil_cpf',
        role: 'company_legal_representative'
    };

    const peoples = [managerInfo, financialManagerInfo, companyLegalRepresentative];

    if(!!consultantId){
        const selectedConsultant = consultants.find(consultant => consultant.id ===  consultantId);
        if (!selectedConsultant) return peoples;
        peoples.push(selectedConsultant);
    };

    return peoples;
};

const makeSubscriptionAddress = ({ address }) => {
    
    const { value: companyCity } = address.matrixcity || {};
    const { value: companyState } = address.matrixstate || {};

    const { value: deliveryCity } = address.deliverycity || {};
    const { value: deliveryState } = address.deliverystate || {};
    
    const company_address = {
        type: 'billing',
        phone_number: address.matrixlandline,
        street: address.matrixstreetAddress,
        number: address.matrixnumber,
        additional_details: address.matrixa,
        zipcode: address.matrixzipCode,
        neighborhood: address.matrixneighborhood,
        city: companyCity,
        state: companyState,
        country: 'BR'
    };

    const delivery_address = address.deliveryuseSameAddress ? {
        ...company_address,
        type: 'other'
    } : {
        type: 'other',
        phone_number: address.matrixlandline,
        street: address.deliverystreetAddress,
        number: address.deliverynumber,
        additional_details: address.deliverya,
        zipcode: address.deliveryzipCode,
        neighborhood: address.deliveryneighborhood,
        city: deliveryCity,
        state: deliveryState,
        country: 'BR'
    };
    
    return [company_address, delivery_address];
};

const makeSubscriptionItemByType = ({ items }) => {
    const itemByTypeId = items.map(item => {
        const [itemType, itemQuatity, itemSpecification] = item || [];

        const itemTypeId = subscriptionItems[itemType];

        if(!itemTypeId){
            return false;
        };

        const formattedItem = {
            contele_product_id: itemTypeId,
            quantity: Number(itemQuatity)
        };

        if(itemSpecification){
            formattedItem.specification = itemSpecification;
        };

        return formattedItem;
    });

    const hasAllSearchedItems = itemByTypeId.every(Boolean);

    if(!hasAllSearchedItems){
        throw new Error('Missing product');
    };

    return itemByTypeId;
};